/**
 * Store Command Center
 *
 * Unified "single pane of glass" dashboard for all store management.
 * Merges Store Monitor + Store Settings with a premium dark mode fintech aesthetic.
 *
 * Features:
 * - Real-time health signals for each store
 * - Glowing status indicators (green = open, red = closed)
 * - Quick access to store details, inventory, and configuration
 * - Global summary metrics
 * - Per-store admin visibility toggles (independent of consumer isActive)
 *
 * @module StoreCommandCenter
 */

import React, { useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Skeleton,
  Alert,
  AlertTitle,
  Button,
  IconButton,
  Tooltip,
  Chip,
  Popover,
  Checkbox,
  FormControlLabel,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Settings as SettingsIcon,
  Store as StoreIcon,
  LocalShipping as TransitIcon,
  Inventory2 as PackingIcon,
  HourglassEmpty as WaitingIcon,
  CheckCircle as OpenIcon,
  DoNotDisturbOn as ClosedIcon,
  PauseCircle as PausedIcon,
  Warning as AlertIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  ArrowDropDown as ArrowDropDownIcon,
  ViewList as RowsIcon,
  GridView as CardsIcon,
} from '@mui/icons-material';
import { Title, useRedirect } from 'react-admin';
import { APP_BAR_HEIGHT } from '../../admin-theme';

import { useCommandCenter } from './hooks/useCommandCenter';
import { useStoreVisibility } from '../../hooks/useStoreVisibility';
import StoreCard from './components/StoreCard';
import StoreDetail from './components/StoreDetail';
import StoreRows, { splitStoreName } from './components/StoreRows';
import { PublicHolidayManager } from '../../components/PublicHolidayManager';
import { NavGlyph } from '../../components/navGlyphs';

// ============================================================================
// Constants
// ============================================================================

const AUTO_REFRESH_INTERVAL = 30000; // 30 seconds

/** Rows scale to 40+ stores; cards are kept for wall displays and small counts. */
type ViewMode = 'rows' | 'cards';
const VIEW_STORAGE_KEY = 'storeCommandCenter.view';

// ============================================================================
// Styles
// ============================================================================

/** Dark mode fintech color palette */
const colors = {
  bgDeepest: 'var(--canvas)',
  bgCard: 'var(--panel)',
  bgElevated: 'var(--raised)',
  textPrimary: 'var(--tx)',
  textSecondary: 'var(--tx2)',
  textMuted: 'var(--tx3)',
  border: 'var(--line-soft)',
  successGlow: '#00FF88',
  errorGlow: '#FF003C',
  warning: '#FFB800',
  info: '#00B4FF',
};

/** Glassmorphism header style */
const glassHeaderStyle = {
  background: 'color-mix(in srgb, var(--panel) 86%, transparent)',
  backdropFilter: 'blur(12px)',
  borderBottom: '1px solid var(--line-soft)',
  position: 'sticky' as const,
  // Sit below the fixed 56px app bar; top: 0 parks it underneath, invisible.
  top: APP_BAR_HEIGHT,
  zIndex: 10,
};

// ============================================================================
// Store status filter
// ============================================================================

/** Which stores the header cards narrow the grid to. */
type StatusFilter = 'all' | 'open' | 'closed' | 'paused';

/**
 * A store is PAUSED when its staff paused it from the order tablet, OPEN when
 * it's active and trading, CLOSED when it's active but not trading (hours,
 * cutoff). Deactivated ("coming soon") stores are none of the three — they
 * show under All only, and never as paused: paused is "not now", not "not yet".
 */
function matchesStatus(store: { isActive: boolean; isOpen: boolean; isPaused: boolean }, filter: StatusFilter): boolean {
  if (filter === 'all') return true;
  if (filter === 'paused') return store.isPaused;
  if (filter === 'open') return store.isActive && store.isOpen;
  return store.isActive && !store.isOpen && !store.isPaused; // closed
}

// ============================================================================
// Sub-components
// ============================================================================

interface SummaryCardProps {
  icon: React.ReactElement;
  label: string;
  value: number;
  color: string;
  subtitle?: string;
  /** Filters the store list to this status when clicked. */
  onClick?: () => void;
  /** True when this card is the active store filter. */
  selected?: boolean;
}

/**
 * Summary stat card for the dashboard header.
 * Doubles as the store-list filter — clicking one narrows the grid below.
 */
function SummaryCard({ icon, label, value, color, subtitle, onClick, selected }: SummaryCardProps) {
  return (
    <Paper
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      aria-pressed={onClick ? !!selected : undefined}
      sx={{
        p: 2,
        height: '100%',
        minHeight: 80,
        cursor: onClick ? 'pointer' : 'default',
        bgcolor: selected ? `${color}14` : colors.bgCard,
        border: `1px solid ${selected ? color : colors.border}`,
        boxShadow: selected ? `0 0 20px ${color}25` : 'none',
        borderRadius: 2,
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        transition: 'all 0.2s',
        '&:hover': {
          bgcolor: colors.bgElevated,
          borderColor: `${color}40`,
          boxShadow: `0 0 20px ${color}15`,
        },
      }}
    >
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: `${color}15`,
          color: color,
        }}
      >
        {icon}
      </Box>
      <Box>
        <Typography variant="caption" sx={{ color: colors.textMuted, display: 'block' }}>
          {label}
        </Typography>
        <Typography variant="h5" fontWeight={700} sx={{ color: colors.textPrimary }}>
          {value}
        </Typography>
        {subtitle && (
          <Typography variant="caption" sx={{ color: colors.textSecondary }}>
            {subtitle}
          </Typography>
        )}
      </Box>
    </Paper>
  );
}

/**
 * Loading skeleton for the dashboard
 */
function DashboardSkeleton() {
  return (
    <Box sx={{ p: 3, bgcolor: colors.bgDeepest, minHeight: '100vh' }}>
      <Skeleton variant="text" width={280} height={40} sx={{ bgcolor: colors.bgCard }} />
      <Skeleton variant="text" width={180} height={20} sx={{ bgcolor: colors.bgCard, mb: 3 }} />

      <Grid container spacing={2} sx={{ mb: 4 }}>
        {[1, 2, 3, 4].map((i) => (
          <Grid size={{ xs: 6, md: 3 }} key={i}>
            <Skeleton variant="rounded" height={100} sx={{ bgcolor: colors.bgCard }} />
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3}>
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <Grid size={{ xs: 12, md: 6, lg: 4 }} key={i}>
            <Skeleton variant="rounded" height={280} sx={{ bgcolor: colors.bgCard, borderRadius: 3 }} />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}

// ============================================================================
// Main Component
// ============================================================================

/**
 * Store Command Center Dashboard
 */
export default function StoreCommandCenter(): React.ReactElement {
  const redirect = useRedirect();
  const { showAll, visibleInactiveIds, isStoreVisible, setShowAll, toggleStoreVisibility } = useStoreVisibility();
  const {
    data,
    isLoading,
    error,
    lastUpdated,
    refresh,
    selectedStoreId,
    setSelectedStoreId,
  } = useCommandCenter(AUTO_REFRESH_INTERVAL);

  const [visibilityAnchor, setVisibilityAnchor] = useState<HTMLElement | null>(null);
  // Public Holiday Manager — moved here from the Admin Tools rail (Jason,
  // 2026-09-14): holidays are per-store trading rules, this page's subject.
  const [holidayManagerOpen, setHolidayManagerOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [view, setView] = useState<ViewMode>(() => {
    const saved = typeof window !== 'undefined' ? window.localStorage.getItem(VIEW_STORAGE_KEY) : null;
    return saved === 'cards' ? 'cards' : 'rows';
  });

  const changeView = (next: ViewMode) => {
    setView(next);
    try {
      window.localStorage.setItem(VIEW_STORAGE_KEY, next);
    } catch {
      // Private mode / storage disabled — the toggle still works for this session.
    }
  };

  const handleStoreClick = (storeId: string) => {
    setSelectedStoreId(storeId);
  };

  // Loading state
  if (isLoading && !data) {
    return <DashboardSkeleton />;
  }

  // Error state
  if (error && !data) {
    return (
      <Box sx={{ p: 3, bgcolor: colors.bgDeepest, minHeight: '100vh' }}>
        <Alert
          severity="error"
          sx={{
            bgcolor: 'rgba(255, 0, 60, 0.1)',
            border: `1px solid ${colors.errorGlow}40`,
            borderRadius: 2,
            '& .MuiAlert-icon': { color: colors.errorGlow },
          }}
          action={
            <Button color="inherit" size="small" onClick={refresh}>
              Retry
            </Button>
          }
        >
          <AlertTitle>Failed to load command center</AlertTitle>
          {error}
        </Alert>
      </Box>
    );
  }

  // Summary from backend (computed over ALL stores — correct)
  const summary = data?.summary ?? null;
  // All stores from API (includes inactive)
  const allStores = data?.stores ?? [];
  // Inactive stores for the dropdown
  const inactiveStores = allStores.filter((s) => !s.isActive);
  // Visible stores for rendering (respects visibility toggles + the status filter).
  // Asking for Paused explicitly overrides the visibility toggle — otherwise the
  // toggle hides inactive stores and the Paused filter would render an empty grid.
  const visibleStores = allStores
    .filter((s) => (statusFilter === 'paused' ? true : isStoreVisible(s)))
    .filter((s) => matchesStatus(s, statusFilter));

  // Paused = the tablet's pause. Closed = active, not paused, just shut right now (hours/cutoff).
  const pausedCount = summary?.pausedStores ?? 0;
  const closedCount = summary ? Math.max(0, summary.activeStores - summary.openStores - pausedCount) : 0;
  // Three cards across when nothing is paused, four when something is.
  const summaryCardSize = pausedCount > 0 ? 3 : 4;
  // Selected store (search all, not just visible)
  const selectedStore = allStores.find((s) => s.id === selectedStoreId);

  // Eye icon state: grey (none), amber (some), bright (all)
  const hasIndividualSelections = visibleInactiveIds.length > 0;
  const eyeColor = showAll ? colors.info : hasIndividualSelections ? colors.warning : colors.textSecondary;
  const eyeBorder = showAll ? colors.info + '60' : hasIndividualSelections ? colors.warning + '60' : colors.border;

  return (
    <Box sx={{ bgcolor: colors.bgDeepest, minHeight: '100vh' }}>
      <Title title="Store Command Center" />

      {/* Glassmorphism Header */}
      <Box sx={{ ...glassHeaderStyle, px: 3, py: 2.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: colors.textPrimary,
                letterSpacing: '-0.02em',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <StoreIcon sx={{ fontSize: 32 }} />
              Store Command Center
            </Typography>
            <Typography variant="body2" component="div" sx={{ color: colors.textSecondary, mt: 0.5 }}>
              {lastUpdated
                ? `Last updated: ${lastUpdated.toLocaleTimeString()}`
                : 'Loading...'}
              {' • '}
              <Chip
                label="Auto-refresh 30s"
                size="small"
                sx={{
                  height: 20,
                  fontSize: '0.65rem',
                  bgcolor: 'rgba(0, 180, 255, 0.15)',
                  color: colors.info,
                  border: `1px solid ${colors.info}30`,
                }}
              />
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
            {/* Rows / cards. Rows is the default — cards don't scale past a screenful. */}
            <ToggleButtonGroup
              size="small"
              exclusive
              value={view}
              onChange={(_, next) => next && changeView(next as ViewMode)}
              sx={{
                '& .MuiToggleButton-root': {
                  color: colors.textSecondary,
                  borderColor: colors.border,
                  px: 1.25,
                  '&.Mui-selected': {
                    color: colors.textPrimary,
                    bgcolor: colors.bgElevated,
                    '&:hover': { bgcolor: colors.bgElevated },
                  },
                },
              }}
            >
              <ToggleButton value="rows" aria-label="List view">
                <RowsIcon fontSize="small" />
              </ToggleButton>
              <ToggleButton value="cards" aria-label="Card view">
                <CardsIcon fontSize="small" />
              </ToggleButton>
            </ToggleButtonGroup>

            {/* Visibility split button — only useful once a store has been deactivated. */}
            <Box sx={{ display: inactiveStores.length > 0 ? 'flex' : 'none', alignItems: 'center' }}>
              <Tooltip title={showAll ? 'Showing all stores' : hasIndividualSelections ? `Showing ${visibleInactiveIds.length} hidden store(s)` : 'Show hidden stores'}>
                <IconButton
                  onClick={() => setShowAll(!showAll)}
                  sx={{
                    color: eyeColor,
                    bgcolor: colors.bgCard,
                    border: `1px solid ${eyeBorder}`,
                    borderRadius: '8px 0 0 8px',
                    '&:hover': { bgcolor: colors.bgElevated, color: colors.textPrimary },
                  }}
                >
                  {showAll || hasIndividualSelections ? <VisibilityIcon /> : <VisibilityOffIcon />}
                </IconButton>
              </Tooltip>
              <Tooltip title="Select individual stores to show">
                <IconButton
                  onClick={(e) => setVisibilityAnchor(e.currentTarget)}
                  sx={{
                    color: eyeColor,
                    bgcolor: colors.bgCard,
                    border: `1px solid ${eyeBorder}`,
                    borderLeft: 'none',
                    borderRadius: '0 8px 8px 0',
                    px: 0.5,
                    minWidth: 28,
                    '&:hover': { bgcolor: colors.bgElevated, color: colors.textPrimary },
                  }}
                >
                  <ArrowDropDownIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>

            {/* Visibility Dropdown Popover */}
            <Popover
              open={Boolean(visibilityAnchor)}
              anchorEl={visibilityAnchor}
              onClose={() => setVisibilityAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              slotProps={{
                paper: {
                  sx: {
                    bgcolor: colors.bgCard,
                    border: `1px solid ${colors.border}`,
                    borderRadius: 2,
                    minWidth: 260,
                    maxHeight: 400,
                  },
                },
              }}
            >
              <Box sx={{ p: 2, borderBottom: `1px solid ${colors.border}` }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={showAll}
                      onChange={(e) => setShowAll(e.target.checked)}
                      sx={{ color: colors.textSecondary, '&.Mui-checked': { color: colors.info } }}
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ color: colors.textPrimary, fontWeight: 600 }}>
                      Show all hidden stores
                    </Typography>
                  }
                />
              </Box>
              {inactiveStores.length > 0 ? (
                <List dense sx={{ py: 0 }}>
                  {inactiveStores.map((store) => (
                    <ListItem key={store.id} disablePadding>
                      <ListItemButton
                        onClick={() => toggleStoreVisibility(store.id)}
                        disabled={showAll}
                        sx={{
                          py: 0.5,
                          '&:hover': { bgcolor: colors.bgElevated },
                          '&.Mui-disabled': { opacity: 0.5 },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <Checkbox
                            edge="start"
                            checked={showAll || visibleInactiveIds.includes(store.id)}
                            disabled={showAll}
                            size="small"
                            sx={{ color: colors.textSecondary, '&.Mui-checked': { color: colors.warning } }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={store.name}
                          primaryTypographyProps={{ variant: 'body2', sx: { color: colors.textPrimary } }}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Box sx={{ p: 2 }}>
                  <Typography variant="body2" sx={{ color: colors.textMuted }}>
                    No hidden stores
                  </Typography>
                </Box>
              )}
            </Popover>

            <Tooltip title="Refresh data">
              <IconButton
                onClick={refresh}
                disabled={isLoading}
                sx={{
                  color: colors.textSecondary,
                  bgcolor: colors.bgCard,
                  border: `1px solid ${colors.border}`,
                  '&:hover': {
                    bgcolor: colors.bgElevated,
                    color: colors.textPrimary,
                  },
                }}
              >
                <RefreshIcon sx={{ animation: isLoading ? 'spin 1s linear infinite' : 'none' }} />
              </IconButton>
            </Tooltip>

            <Tooltip title="Public Holiday Manager">
              <IconButton
                onClick={() => setHolidayManagerOpen(true)}
                aria-label="Public Holiday Manager"
                sx={{
                  color: colors.textSecondary,
                  bgcolor: colors.bgCard,
                  border: `1px solid ${colors.border}`,
                  '& svg': { width: 20, height: 20 },
                  '&:hover': {
                    bgcolor: colors.bgElevated,
                    color: colors.textPrimary,
                  },
                }}
              >
                <NavGlyph name="publicHolidayManager" />
              </IconButton>
            </Tooltip>

            <Button
              variant="outlined"
              startIcon={<SettingsIcon />}
              onClick={() => redirect('/stores/defaults')}
              sx={{
                color: colors.warning,
                borderColor: `${colors.warning}60`,
                bgcolor: `${colors.warning}10`,
                fontWeight: 600,
                textTransform: 'none',
                px: 2,
                '&:hover': {
                  bgcolor: `${colors.warning}20`,
                  borderColor: colors.warning,
                  boxShadow: `0 0 15px ${colors.warning}30`,
                },
              }}
            >
              System Defaults
            </Button>
          </Box>
        </Box>

        {/* Global summary cards. Paused only earns a slot when something is
            actually paused — otherwise it's a quarter of the header showing 0. */}
        {summary && (
          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid size={{ xs: 6, md: summaryCardSize }}>
              <SummaryCard
                icon={<StoreIcon />}
                label="Total Stores"
                value={summary.totalStores}
                color={colors.info}
                onClick={() => setStatusFilter('all')}
                selected={statusFilter === 'all'}
              />
            </Grid>
            <Grid size={{ xs: 6, md: summaryCardSize }}>
              <SummaryCard
                icon={<OpenIcon />}
                label="Open Now"
                value={summary.openStores}
                color={colors.successGlow}
                subtitle={`of ${summary.activeStores} active`}
                onClick={() => setStatusFilter('open')}
                selected={statusFilter === 'open'}
              />
            </Grid>
            <Grid size={{ xs: 6, md: summaryCardSize }}>
              <SummaryCard
                icon={<ClosedIcon />}
                label="Closed Now"
                value={closedCount}
                color={colors.errorGlow}
                subtitle="active, not trading"
                onClick={() => setStatusFilter('closed')}
                selected={statusFilter === 'closed'}
              />
            </Grid>
            {pausedCount > 0 && (
              <Grid size={{ xs: 6, md: summaryCardSize }}>
                <SummaryCard
                  icon={<PausedIcon />}
                  label="Paused Now"
                  value={pausedCount}
                  color={colors.warning}
                  subtitle="paused from the tablet"
                  onClick={() => setStatusFilter('paused')}
                  selected={statusFilter === 'paused'}
                />
              </Grid>
            )}
          </Grid>
        )}
      </Box>

      {/* Main Content */}
      <Box sx={{ p: 3 }}>
        {/* Browse: rows by default, cards behind the toggle. */}
        {!selectedStoreId && (
          view === 'rows' ? (
            <StoreRows
              stores={visibleStores}
              selectedStoreId={selectedStoreId}
              onSelect={handleStoreClick}
            />
          ) : (
            <Grid container spacing={3}>
              {visibleStores.map((store) => (
                <Grid size={{ xs: 12, md: 6, lg: 4 }} key={store.id}>
                  <StoreCard
                    store={store}
                    onClick={handleStoreClick}
                    isSelected={store.id === selectedStoreId}
                  />
                </Grid>
              ))}
            </Grid>
          )
        )}

        {/* Detail. The tabs inside need real width, so rather than shrinking them
            into a side panel we keep a narrow store rail alongside — you can hop
            between branches without losing the board. */}
        {selectedStore && (
          <Box sx={{ display: 'flex', gap: 2.5, alignItems: 'flex-start' }}>
            <Box
              sx={{
                width: 232,
                flexShrink: 0,
                display: { xs: 'none', lg: 'block' },
                maxHeight: 'calc(100vh - 260px)',
                overflowY: 'auto',
                border: `1px solid ${colors.border}`,
                borderRadius: 2,
                bgcolor: colors.bgCard,
                py: 0.5,
              }}
            >
              {visibleStores.map((store) => {
                const active = store.id === selectedStoreId;
                return (
                  <Box
                    key={store.id}
                    onClick={() => setSelectedStoreId(store.id)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      px: 1.5,
                      py: 0.9,
                      cursor: 'pointer',
                      bgcolor: active ? colors.bgElevated : 'transparent',
                      boxShadow: active ? `inset 3px 0 0 ${colors.info}` : 'none',
                      '&:hover': { bgcolor: colors.bgElevated },
                    }}
                  >
                    <Box
                      sx={{
                        width: 7,
                        height: 7,
                        borderRadius: '50%',
                        flexShrink: 0,
                        bgcolor: !store.isActive
                          ? colors.warning
                          : store.isOpen
                            ? colors.successGlow
                            : colors.errorGlow,
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{
                        color: active ? colors.textPrimary : colors.textSecondary,
                        fontWeight: active ? 650 : 400,
                        fontSize: '0.8rem',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {splitStoreName(store.name).branch}
                    </Typography>
                  </Box>
                );
              })}
            </Box>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <StoreDetail
                store={selectedStore}
                onClose={() => setSelectedStoreId(null)}
              />
            </Box>
          </Box>
        )}

        {/* Empty state */}
        {visibleStores.length === 0 && !isLoading && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 400,
            }}
          >
            <StoreIcon sx={{ fontSize: 64, color: colors.textMuted, mb: 2 }} />
            <Typography variant="h6" sx={{ color: colors.textSecondary }}>
              No active stores
            </Typography>
            <Typography variant="body2" sx={{ color: colors.textMuted }}>
              Stores will appear here when they are set to active. Use the eye icon to reveal hidden stores.
            </Typography>
          </Box>
        )}
      </Box>

      <PublicHolidayManager open={holidayManagerOpen} onClose={() => setHolidayManagerOpen(false)} />

      {/* Keyframe animations */}
      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </Box>
  );
}
